import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import ClearIcon from "@mui/icons-material/Close";

import { get } from "lib/api";
import { openOrCreatePurchaseReceiptForPo } from "services/purchaseReceipts";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosList, IosListItem, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  !d
    ? null
    : d.attributes
    ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes }
    : { ...d, documentId: d.documentId || d.id };

const normalizeArray = (rel) => {
  if (!rel) return [];
  if (Array.isArray(rel)) return rel.map(normalize);
  if (Array.isArray(rel?.data)) return rel.data.map(normalize);
  return [];
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "—");
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "—");

export default function WarehouseReceivingMobile() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const tab = String(searchParams.get("tab") || "").trim().toLowerCase() === "cores" ? "cores" : "pos";
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const setTab = (next) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === "cores") nextParams.set("tab", "cores");
    else nextParams.delete("tab");
    setSearchParams(nextParams, { replace: true });
    setPage(1);
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["m-warehouse-receiving-pos", search, page, pageSize],
    queryFn: () =>
      get("/api/purchase-orders", {
        publicationState: "live",
        populate: {
          vendor: { fields: ["name"] },
          items: {
            fields: ["id", "documentId"],
            populate: { inventoryItem: { fields: ["sku", "name"] } },
          },
        },
        sort: "expectedDeliveryDate:asc",
        filters: {
          status: { $in: ["open", "partially_received"] },
          ...(search && { $or: [{ purchaseOrderNumber: { $containsi: search } }] }),
        },
        pagination: { page, pageSize },
      }),
    enabled: tab === "pos",
    staleTime: 10_000,
  });

  const coresQuery = useQuery({
    queryKey: ["m-inbound-cores", search, page, pageSize],
    queryFn: () =>
      get("/api/core-returns/warehouse/inbound", {
        search: search || undefined,
        page,
        pageSize,
        status: "incoming",
      }),
    enabled: tab === "cores",
    staleTime: 10_000,
  });

  const rows = useMemo(
    () =>
      (data?.data || []).map((row) => {
        const po = normalize(row);
        return {
          ...po,
          vendor: normalize(po?.vendor?.data || po?.vendor || null),
          items: normalizeArray(po?.items),
        };
      }),
    [data]
  );

  const coreRows = useMemo(() => (Array.isArray(coresQuery.data?.data) ? coresQuery.data.data : []), [coresQuery.data]);

  const pageCount =
    tab === "cores"
      ? coresQuery.data?.meta?.pagination?.pageCount || 1
      : data?.meta?.pagination?.pageCount || 1;
  const total =
    tab === "cores" ? coresQuery.data?.meta?.pagination?.total : data?.meta?.pagination?.total;
  const poKeys = useMemo(
    () => rows.flatMap((po) => [po.documentId, po.id]).filter(Boolean).map((value) => String(value)),
    [rows]
  );

  const { data: activeReceiptsData } = useQuery({
    queryKey: ["m-warehouse-active-receipts", poKeys.join(",")],
    queryFn: async () => {
      if (!poKeys.length) return [];
      const docIds = [...new Set(poKeys.map((value) => String(value || "").trim()).filter(Boolean))];
      const resp = await get("/api/purchase-receipts", {
        publicationState: "preview",
        sort: "updatedAt:desc",
        pagination: { page: 1, pageSize: Math.max(50, rows.length * 3) },
        fields: ["ticketNumber", "status", "receivedBy", "receivedAt", "updatedAt", "id", "documentId"],
        populate: { purchaseOrder: { fields: ["id", "documentId"] } },
        filters: {
          status: { $in: ["draft", "in_review"] },
          purchaseOrder: { documentId: { $in: docIds } },
        },
      });
      const raw = Array.isArray(resp?.data) ? resp.data : resp?.data?.data || [];
      return raw.map(normalize);
    },
    enabled: tab === "pos" && poKeys.length > 0,
    staleTime: 10_000,
  });

  const activeReceiptByPoKey = useMemo(() => {
    const map = {};
    (activeReceiptsData || []).forEach((receipt) => {
      const po = normalize(receipt?.purchaseOrder?.data || receipt?.purchaseOrder || null);
      [po?.documentId, po?.id].filter(Boolean).forEach((key) => {
        if (!map[String(key)]) map[String(key)] = receipt;
      });
    });
    return map;
  }, [activeReceiptsData]);

  const openMutation = useMutation({
    mutationFn: async (purchaseOrder) => {
      const poDocId = purchaseOrder?.documentId || purchaseOrder?.id;
      if (!poDocId) throw new Error("Purchase order not found.");
      const result = await openOrCreatePurchaseReceiptForPo(poDocId);
      if (!result?.ok) throw new Error(result?.error || "Failed to open receipt.");
      const receiptId = result?.purchaseReceipt?.documentId || result?.purchaseReceipt?.id;
      if (!receiptId) throw new Error("Receipt opened but no ID was returned.");
      return receiptId;
    },
    onSuccess: (receiptId) => {
      queryClient.invalidateQueries({ queryKey: ["m-warehouse-receiving-pos"] });
      navigate(`/m/warehouse/receiving/${receiptId}`);
    },
    onError: (e) => alert(e?.message || "Failed to open receiving ticket."),
  });

  const loading = tab === "cores" ? coresQuery.isLoading : isLoading;
  const fetching = tab === "cores" ? coresQuery.isFetching : isFetching;
  const loadError = tab === "cores" ? coresQuery.error : error;

  return (
    <IosPage>
      <MobileTopBar
        title="Receiving"
        subtitle={
          typeof total === "number"
            ? `${total} ${tab === "cores" ? "incoming" : "open"} • page ${page}/${pageCount}`
            : fetching
            ? "Syncing..."
            : "Warehouse"
        }
        onBack={() => navigate("/m/dashboard")}
      />

      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={tab}
        onChange={(_e, next) => {
          if (next) setTab(next);
        }}
        sx={{ mt: 1 }}
      >
        <ToggleButton value="pos" sx={{ textTransform: "none", fontWeight: 800 }}>
          Purchase orders
        </ToggleButton>
        <ToggleButton value="cores" sx={{ textTransform: "none", fontWeight: 800 }}>
          Incoming cores
        </ToggleButton>
      </ToggleButtonGroup>

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          placeholder={tab === "cores" ? "Search tracking, customer, unit…" : "Search PO number..."}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small">search</Icon>
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Stack>

      {loading ? <LinearProgress sx={{ mt: 1 }} /> : null}
      {loadError ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {tab === "cores" ? "Failed to load incoming cores." : "Failed to load open purchase orders."}
        </Typography>
      ) : null}

      {tab === "cores" ? (
        <IosList sx={{ mt: 1 }}>
          {coreRows.map((row) => (
            <IosListItem
              key={row.documentId}
              icon={row.received ? "inventory" : "assignment_return"}
              title={row.customer?.name || "Customer"}
              meta={[
                row.trackingNumber || row.documentId?.slice?.(0, 10),
                row.shippingCarrier || null,
                [row.unit?.optionalTag, row.unit?.serialNumber].filter(Boolean).join(" · ") || null,
                row.warehouseInspectedAt ? `Recv ${formatDateTime(row.warehouseInspectedAt)}` : null,
              ]
                .filter(Boolean)
                .join(" • ")}
              trailing={row.received ? "view" : "receive"}
              accent={row.received ? "green" : "orange"}
              onClick={() => navigate(`/m/warehouse/receiving/cr-${row.documentId}`)}
            />
          ))}
          {!loading && !coreRows.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No incoming cores with carrier shipping or an in-person drop-off yet.
            </Typography>
          ) : null}
        </IosList>
      ) : (
        <IosList sx={{ mt: 1 }}>
          {rows.map((po) => {
            const receipt =
              activeReceiptByPoKey[String(po.documentId || "")] ||
              activeReceiptByPoKey[String(po.id || "")] ||
              null;
            const warehouseDone =
              !!receipt && (receipt.status === "in_review" || !!receipt.receivedBy || !!receipt.receivedAt);
            const subtitle = [
              po.vendor?.name || null,
              po.expectedDeliveryDate ? `ETA ${formatDate(po.expectedDeliveryDate)}` : null,
              `${Array.isArray(po.items) ? po.items.length : 0} items`,
            ]
              .filter(Boolean)
              .join(" • ");

            return (
              <IosListItem
                key={po.documentId || po.id}
                icon={warehouseDone ? "inventory" : receipt?.status === "draft" ? "pending_actions" : "move_to_inbox"}
                title={po.purchaseOrderNumber || `PO #${po.id}`}
                meta={subtitle || "Open purchase order"}
                trailing={warehouseDone ? "received" : receipt?.status === "draft" ? "resume" : "receive"}
                disabled={warehouseDone || openMutation.isPending}
                onClick={() => openMutation.mutate(po)}
                accent={warehouseDone ? "green" : "orange"}
              />
            );
          })}

          {!loading && !rows.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No open purchase orders found.
            </Typography>
          ) : null}
        </IosList>
      )}

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination size="small" count={pageCount} page={page} onChange={(e, nextPage) => setPage(nextPage)} />
        </Box>
      ) : null}
    </IosPage>
  );
}
